export interface InjectOptions {
  position?: 'BL'|'BR'|'TL'|'TR';
  width?: string;
  height?: string;
  /** Optional `allow=` attribute. Set to "microphone" only when voice is enabled. (I11) */
  allow?: string;
  /** Parent node to append the iframe into. Defaults to document.body.
   *  Required for `inline` preset to avoid the late-reparent bug:
   *  appending to body first then `parent.appendChild(iframe)` later
   *  forces Chrome / Firefox to abort the iframe's load mid-stream,
   *  leaving the chat blank until the user closes and reopens. */
  parent?: HTMLElement;
}

export function injectIframe(origin: string, flowSlug: string, opts: InjectOptions = {}): HTMLIFrameElement {
  const iframe = document.createElement('iframe');
  // I11 — only request microphone when explicitly opted in.
  if (opts.allow) iframe.allow = opts.allow;
  // I10 — sandbox the iframe to the minimum needed permissions.
  // allow-same-origin is required for the iframe to open a WebSocket to its own origin.
  // allow-popups-to-escape-sandbox dropped per adversarial review:
  // combined with allow-same-origin + allow-scripts it would neutralise the
  // sandbox for any popup the iframe spawned (the popup escapes the sandbox
  // entirely while still inheriting same-origin status). We don't currently
  // spawn OAuth popups from inside the chat iframe; if that becomes needed,
  // re-introduce it with documented scope then.
  iframe.setAttribute(
    'sandbox',
    'allow-scripts allow-same-origin allow-forms allow-popups'
  );
  iframe.title = 'Apptor chat';
  iframe.style.cssText = `
    position: fixed; border: 0; z-index: 2147483646;
    width: ${opts.width || '380px'}; height: ${opts.height || '560px'};
    box-shadow: 0 24px 60px rgba(0,0,0,0.35); border-radius: 16px;
    background: transparent; display: none;
  `;
  const pos = opts.position || 'BR';
  if (pos === 'BR') { iframe.style.bottom = '24px'; iframe.style.right = '24px'; }
  else if (pos === 'BL') { iframe.style.bottom = '24px'; iframe.style.left = '24px'; }
  else if (pos === 'TR') { iframe.style.top = '24px'; iframe.style.right = '24px'; }
  else { iframe.style.top = '24px'; iframe.style.left = '24px'; }
  // Append BEFORE setting src so the iframe load happens with a stable
  // parent and src in one go. Setting src AFTER append() causes some
  // browsers to start loading from the previous parent context.
  (opts.parent || document.body).appendChild(iframe);
  iframe.src = `${origin}/w/${encodeURIComponent(flowSlug)}`;
  return iframe;
}

export function showIframe(iframe: HTMLIFrameElement): void { iframe.style.display = 'block'; }
export function hideIframe(iframe: HTMLIFrameElement): void { iframe.style.display = 'none'; }
